import { useState } from 'react';
import { supabase } from './supabase';
import { ThemeStyles, C, primaryFill, primaryText } from './theme';

// Login / Register screen. Shown when there is no active session.
export default function Auth({ lang = 'zh', onToggleLang, theme = 'light', onToggleTheme }) {
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);       // { type: 'error'|'info', text }
  const [showResend, setShowResend] = useState(false); // 邮箱未验证时显示"重发验证邮件"

  const T = {
    zh: {
      brand: '💰 EasyMoney',
      login: '登录', register: '注册',
      email: '邮箱', password: '密码',
      loginBtn: '登录', registerBtn: '注册',
      toRegister: '还没有账号？去注册', toLogin: '已有账号？去登录',
      processing: '处理中...',
      needEmailPwd: '请输入邮箱和密码',
      pwdTooShort: '密码至少 6 位',
      registerOk: '注册成功！请用刚才的邮箱密码登录。',
      checkPending: '账号已注册，等待管理员审核后即可使用。',
      // 错误提示（中文）
      errEmailNotConfirmed: '邮箱未验证，请先查收验证邮件并点击确认链接（含垃圾邮件箱）。',
      errInvalidCredentials: '邮箱或密码错误，请重新输入。',
      errUserExists: '该邮箱已注册，请直接登录。',
      errPwdShort: '密码至少需要 6 位。',
      errRateLimit: '操作太频繁，请稍后再试。',
      errInvalidEmail: '邮箱格式不正确。',
      errNetwork: '网络连接失败，请检查网络后重试。',
      errGeneric: '出错了，请稍后再试。',
    },
    en: {
      brand: '💰 EasyMoney',
      login: 'Sign In', register: 'Sign Up',
      email: 'Email', password: 'Password',
      loginBtn: 'Sign In', registerBtn: 'Sign Up',
      toRegister: "No account? Sign up", toLogin: 'Have an account? Sign in',
      processing: 'Processing...',
      needEmailPwd: 'Please enter email and password',
      pwdTooShort: 'Password must be at least 6 characters',
      registerOk: 'Registered! Please sign in with your email and password.',
      checkPending: 'Account created. Waiting for admin approval.',
      // Error messages (English)
      errEmailNotConfirmed: 'Email not verified. Please check your inbox (and spam) and click the confirmation link.',
      errInvalidCredentials: 'Incorrect email or password.',
      errUserExists: 'This email is already registered. Please sign in.',
      errPwdShort: 'Password must be at least 6 characters.',
      errRateLimit: 'Too many attempts. Please try again later.',
      errInvalidEmail: 'Invalid email address.',
      errNetwork: 'Network error. Please check your connection and retry.',
      errGeneric: 'Something went wrong. Please try again.',
    },
  }[lang];

  // 把 Supabase 返回的英文错误，映射成当前语言的友好提示。
  // 没匹配到的，中文界面给通用提示，英文界面保留原文。
  function authErrorText(error) {
    const raw = (error?.message || '').toLowerCase();
    if (raw.includes('email not confirmed')) return T.errEmailNotConfirmed;
    if (raw.includes('invalid login credentials')) return T.errInvalidCredentials;
    if (raw.includes('already registered') || raw.includes('already been registered') || raw.includes('user already exists')) return T.errUserExists;
    if (raw.includes('password should be at least') || raw.includes('at least 6')) return T.errPwdShort;
    if (raw.includes('rate limit') || raw.includes('too many requests') || raw.includes('email rate')) return T.errRateLimit;
    if (raw.includes('invalid email') || raw.includes('unable to validate email')) return T.errInvalidEmail;
    if (raw.includes('network') || raw.includes('failed to fetch')) return T.errNetwork;
    // 没匹配到：中文界面给通用中文提示；英文界面保留原始英文（至少有信息）
    return lang === 'zh' ? T.errGeneric : (error?.message || T.errGeneric);
  }

  // 重新发送验证邮件
  async function handleResend() {
    if (!email) { setMsg({ type: 'error', text: T.needEmailPwd }); return; }
    setBusy(true);
    const { error } = await supabase.auth.resend({ type: 'signup', email });
    setBusy(false);
    if (error) {
      setMsg({ type: 'error', text: authErrorText(error) });
    } else {
      setShowResend(false);
      setMsg({ type: 'info', text: lang === 'zh' ? '验证邮件已重新发送，请查收（含垃圾邮件箱）。' : 'Verification email resent. Please check your inbox (and spam).' });
    }
  }

  async function handleLogin() {
    if (!email || !password) { setMsg({ type: 'error', text: T.needEmailPwd }); return; }
    setBusy(true); setMsg(null);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) {
      setMsg({ type: 'error', text: authErrorText(error) });
      // 邮箱未验证 → 显示"重发验证邮件"按钮
      setShowResend((error?.message || '').toLowerCase().includes('email not confirmed'));
    }
    // On success, the App's auth listener takes over and shows the app.
  }

  async function handleRegister() {
    if (!email || !password) { setMsg({ type: 'error', text: T.needEmailPwd }); return; }
    if (password.length < 6) { setMsg({ type: 'error', text: T.pwdTooShort }); return; }
    setBusy(true); setMsg(null);
    const { error } = await supabase.auth.signUp({ email, password });
    setBusy(false);
    if (error) {
      setMsg({ type: 'error', text: authErrorText(error) });
      return;
    }
    // If email confirmation is off, a session may be returned immediately.
    // Either way, tell the user what to do next.
    setMode('login');
    setMsg({ type: 'info', text: T.registerOk });
  }

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1px solid #ddd',
    borderRadius: 12, fontSize: 14, boxSizing: 'border-box', marginBottom: 12,
    outline: 'none', border: `1.5px solid ${C.border}`, transition: 'all 0.18s ease',
    fontFamily: 'inherit',
  };

  return (
    <div className="em-mesh" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh',
    }}>
      <ThemeStyles mode={theme} key={theme} />
      <div className="em-pop" style={{
        width: 380, background: theme === 'dark' ? 'rgba(21,21,31,0.78)' : 'rgba(255,255,255,0.72)', backdropFilter: 'blur(20px)',
        borderRadius: 24, padding: 38, position: 'relative',
        boxShadow: theme === 'dark' ? '0 20px 60px rgba(0,0,0,0.5)' : '0 20px 60px rgba(91,69,217,0.18), 0 4px 16px rgba(28,27,46,0.06)',
        border: theme === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.6)',
      }}>
        <div style={{ position: 'absolute', top: 18, right: 18, display: 'flex', gap: 8 }}>
          {onToggleTheme && (
            <button type="button" onClick={onToggleTheme} title="切换主题"
              style={{
                background: C.card, border: `1.5px solid ${C.border}`, borderRadius: 9,
                padding: '5px 10px', cursor: 'pointer', fontSize: 13, color: C.inkSoft, fontWeight: 600,
              }}>
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          )}
          {onToggleLang && (
            <button type="button" onClick={onToggleLang}
              style={{
                background: C.card, border: `1.5px solid ${C.border}`, borderRadius: 9,
                padding: '5px 11px', cursor: 'pointer', fontSize: 12, color: C.inkSoft, fontWeight: 600,
              }}>
              {lang === 'zh' ? 'EN' : '中'}
            </button>
          )}
        </div>
        <div className="display" style={{ fontSize: 28, fontWeight: 800, textAlign: 'center', marginBottom: 4, letterSpacing: '-0.8px' }}>
          <span style={{ marginRight: 4 }}>💰</span><span className="em-grad-text">EasyMoney</span>
        </div>
        <div style={{ textAlign: 'center', color: C.inkFaint, fontSize: 13, marginBottom: 28, fontWeight: 500 }}>
          {mode === 'login' ? T.login : T.register}
        </div>

        <input type="email" placeholder={T.email} value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? handleLogin() : handleRegister())}
          style={inputStyle} />
        <input type="password" placeholder={T.password} value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? handleLogin() : handleRegister())}
          style={inputStyle} />

        {msg && (
          <div style={{
            fontSize: 12.5, marginBottom: 12, padding: '10px 12px', borderRadius: 10,
            background: msg.type === 'error' ? C.expenseSoft : C.incomeSoft,
            color: msg.type === 'error' ? C.expense : C.income, fontWeight: 500,
          }}>
            {msg.text}
          </div>
        )}

        {showResend && (
          <button type="button" disabled={busy} onClick={handleResend}
            style={{
              width: '100%', padding: '10px 0', marginBottom: 12, background: C.gradSoft,
              color: C.primary, border: `1.5px solid ${C.primary}`, borderRadius: 12,
              fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
              fontFamily: 'inherit',
            }}>
            {lang === 'zh' ? '📧 重新发送验证邮件' : '📧 Resend verification email'}
          </button>
        )}

        <button type="button" disabled={busy} className="em-btn-primary"
          onClick={mode === 'login' ? handleLogin : handleRegister}
          style={{
            width: '100%', padding: '13px 0', background: primaryFill(), color: primaryText(),
            border: 'none', borderRadius: 12, fontSize: 14.5, fontWeight: 600,
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, marginBottom: 16,
            boxShadow: C.glow, fontFamily: 'inherit', letterSpacing: '0.2px',
          }}>
          {busy ? T.processing : (mode === 'login' ? T.loginBtn : T.registerBtn)}
        </button>

        <div style={{ textAlign: 'center' }}>
          <button type="button"
            onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setMsg(null); setShowResend(false); }}
            style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {mode === 'login' ? T.toRegister : T.toLogin}
          </button>
        </div>
      </div>
    </div>
  );
}
